import { useEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui";

/**
 * Wraps the native <dialog>, which gives us the focus trap, ESC handling and
 * inertness of the rest of the page for free.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const { t } = useTranslation("common");
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onCancel={onClose}
      onClick={(event) => {
        // A click that lands on the dialog itself is its backdrop. Native
        // dialogs already handle Escape; this gives touch users an equivalent
        // dismissal without closing when they interact with the content.
        if (event.target === event.currentTarget) onClose();
      }}
      aria-labelledby="modal-title"
      /*
       * `open:flex` rather than a bare `flex`: a closed <dialog> is hidden by
       * `display: none`, and an unconditional `flex` overrides that and renders
       * the thing inline on the page. The variant only applies while the
       * `open` attribute is present, so the platform keeps its own hiding.
       *
       * `85dvh` and not `85vh` — on iOS Safari `vh` is the viewport *with the
       * address bar retracted*, so a modal sized in `vh` is taller than what
       * you can actually see until you scroll the page behind it.
       */
      className="m-auto max-h-[85dvh] w-[min(32rem,calc(100vw-2rem))] rounded-lg border border-line bg-surface p-0 text-fg backdrop:bg-canvas/40 open:flex open:flex-col"
    >
      <div className="flex shrink-0 items-start justify-between gap-4 border-b border-line px-5 py-4">
        <div className="min-w-0">
          <h2 id="modal-title" className="font-semibold">
            {title}
          </h2>
          {description && (
            <p className="mt-1 text-sm text-fg-subtle">{description}</p>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          icon="close"
          aria-label={t("modal.close")}
          onClick={onClose}
        />
      </div>
      {/*
       * The body is the only part that scrolls, so the title stays readable and
       * the confirm button stays reachable on a phone no matter how long the
       * content is — a plan picker or a list of recovery codes runs well past a
       * 667px screen.
       *
       * -----------------------------------------------------------------------
       * `flex-auto` and not `flex-1`, and the difference is Safari
       * -----------------------------------------------------------------------
       * `flex-1` is `flex: 1 1 0%`. The HTML UA stylesheet sizes a `<dialog>`
       * with `height: fit-content`, so this flex column has no definite height
       * and the browser must size it from its children's max-content
       * contributions.
       *
       * Chrome and Firefox use the body's actual content height for that.
       * **Safari uses the flex basis**, which is `0` — so the body contributes
       * nothing, the dialog collapses to header plus footer, and the content is
       * clipped to a sliver with the footer riding up over it. That is the whole
       * bug: a modal that renders correctly everywhere else and arrives in
       * Safari as an unreadable stub.
       *
       * `flex-auto` is `flex: 1 1 auto`, so the basis is the content and every
       * engine agrees on the height. It still shrinks and scrolls once
       * `max-h-[85dvh]` binds, because `flex-shrink` is 1 and `min-h-0` lets it
       * go below its content — which is the behaviour this element was always
       * relying on and only ever got by accident.
       *
       * Measured before and after in Chrome: identical (398px dialog, 219px
       * body, dialog well under its cap), so this is a no-op everywhere it was
       * already working.
       */}
      <div className="min-h-0 flex-auto overflow-y-auto px-5 py-4">{children}</div>
      {footer && (
        <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-line px-5 py-4">
          {footer}
        </div>
      )}
    </dialog>
  );
}

/**
 * A Modal that asks before a consequential action: remove a member, revoke an
 * invitation, promote to owner, leave an organization. The destructive action
 * is never one click away, and the confirm button carries the danger colour.
 */
export function ConfirmDialog({
  open,
  onClose,
  title,
  description,
  confirmLabel = "Confirm",
  tone = "danger",
  loading = false,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description: ReactNode;
  confirmLabel?: string;
  tone?: "danger" | "primary";
  loading?: boolean;
  onConfirm: () => void;
}) {
  const { t } = useTranslation("common");
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            {t("modal.cancel")}
          </Button>
          <Button
            variant={tone === "danger" ? "danger" : "primary"}
            onClick={onConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-sm leading-6 text-fg-subtle">{description}</p>
    </Modal>
  );
}
